import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  Fade,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import confetti from 'canvas-confetti';
import {
  validateTagCloudData,
  isValidTagCloudData,
  createSampleTagCloudData,
} from '../utils/tagCloudUtils';
import { styled } from '@mui/material/styles';
import Button from './UIKit/Button';
import { useTranslation } from 'react-i18next';

// Стилизованная карточка определения
const DefinitionCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[4],
  minHeight: '120px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  position: 'relative',
}));

// Стилизованная область для перетаскивания
const DropArea = styled(Box)(({ theme }) => ({
  minHeight: '100px',
  border: `2px dashed ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.background.default,
  transition: 'all 0.3s ease',
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(3),
}));

// Стилизованный тег для облака
const CloudTag = styled('div')(({ theme, fontSize, rotate, color }) => ({
  display: 'inline-block',
  padding: theme.spacing(1, 2),
  margin: theme.spacing(0.5),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: color || theme.palette.primary.light,
  color: theme.palette.getContrastText(color || theme.palette.primary.light),
  fontSize: `${fontSize || 1}rem`,
  fontWeight: 500,
  cursor: 'grab',
  transform: `rotate(${rotate || 0}deg)`,
  transition: 'all 0.2s ease',
  boxShadow: `0 2px 4px ${theme.palette.primary.main}40`,
  userSelect: 'none',
  '&:hover': {
    transform: `rotate(${rotate || 0}deg) scale(1.05)`,
    boxShadow: `0 4px 8px ${theme.palette.primary.main}60`,
  },
  '&:active': {
    cursor: 'grabbing',
    transform: `rotate(${rotate || 0}deg) scale(0.98)`,
  },
}));

const TagCloud = ({ options, onChange }) => {
  const { t } = useTranslation(['lessons']);

  const [terms, setTerms] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [droppedTerm, setDroppedTerm] = useState(null);
  const [isCorrect, setIsCorrect] = useState(null); // null, true, или false
  const [error, setError] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [score, setScore] = useState(0);
  const [showNextButton, setShowNextButton] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const cloudRef = useRef(null);
  const [shuffleTrigger, setShuffleTrigger] = useState(0); // Триггер для перемешивания тегов

  // Обрабатываем опции и преобразуем их в корректный формат
  const processedOptions = useMemo(() => {
    try {
      // Используем утилиту валидации для проверки данных
      const validData = validateTagCloudData(options, 'TagCloud компонент');

      // Проверяем, валидны ли данные
      if (validData.length === 0) {
        setError(t('tagCloud.noData'));
        console.warn(t('tagCloud.noData'), options);

        // Если нет данных и это режим разработки, создаем тестовые данные
        if (process.env.NODE_ENV === 'development') {
          console.info(t('tagCloud.creatingTestData'));
          return createSampleTagCloudData(8);
        }
        return [];
      }

      if (!isValidTagCloudData(validData)) {
        console.warn(t('tagCloud.invalidData'), validData);
        setError(t('tagCloud.invalidData'));
        return validData;
      }

      setError(null);
      return validData;
    } catch (err) {
      console.error(t('tagCloud.optionsError'), err);
      setError(t('tagCloud.optionsErrorWithMessage', { message: err.message }));
      return [];
    }
  }, [options, t]);

  // Генерируем случайные стили для тегов
  const cloudTags = useMemo(() => {
    if (!terms.length) return [];

    // Извлекаем все уникальные термины
    const allTerms = [...new Set(terms.map((item) => item.term))];

    // Добавляем дополнительные термины для создания более плотного облака
    const colorsArray = [
      '#4e79a7',
      '#f28e2c',
      '#e15759',
      '#76b7b2',
      '#59a14f',
      '#edc949',
      '#af7aa1',
      '#ff9da7',
      '#9c755f',
      '#bab0ab',
    ];

    // Создаем случайные стили для каждого тега
    return allTerms.map((term, index) => ({
      term,
      fontSize: Math.random() * 1.2 + 0.8, // от 0.8 до 2.0
      rotate: Math.floor(Math.random() * 60 - 30), // от -30 до 30 градусов
      color: colorsArray[index % colorsArray.length],
      left: `${Math.random() * 80}%`,
      top: `${Math.random() * 80}%`,
      zIndex: Math.floor(Math.random() * 10),
    }));
  }, [terms]); // Добавляем shuffleTrigger как зависимость

  // Инициализация терминов
  useEffect(() => {
    if (processedOptions.length > 0) {
      setTerms(processedOptions);
      setCurrentIndex(0);
      setDroppedTerm(null);
      setIsCorrect(null);
      setCompleted(false);
      setScore(0);
    }
  }, [processedOptions]);

  // Функция для создания эффекта салюта при правильном ответе
  const fireworksEffect = () => {
    const defaults = {
      startVelocity: 30,
      spread: 360,
      ticks: 60,
      zIndex: 0,
      disableForReducedMotion: true,
    };

    const randomInRange = (min, max) => Math.random() * (max - min) + min;

    const animationEnd = Date.now() + 800;
    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 30 * (timeLeft / 800);

      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.3, 0.7), y: randomInRange(0.4, 0.6) },
      });
    }, 200);
  };

  // Обработчики перетаскивания
  const handleDragStart = (e, term) => {
    e.dataTransfer.setData('text/plain', term);
    e.dataTransfer.effectAllowed = 'move';

    // Добавляем стиль для элемента при перетаскивании
    if (e.target.style) {
      setTimeout(() => {
        e.target.style.opacity = '0.4';
      }, 0);
    }
  };

  const handleDragEnd = (e) => {
    // Возвращаем стиль элемента
    if (e.target.style) {
      e.target.style.opacity = '1';
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!isDragOver) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  // Функция для перемешивания тегов
  const shuffleTags = () => {
    setShuffleTrigger((prev) => prev + 1);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);

    if (isCorrect !== null || completed) return; // Предотвращаем дроп после ответа или завершения

    const term = e.dataTransfer.getData('text/plain');
    setDroppedTerm(term);

    const currentTerm = terms[currentIndex];
    const correct = term === currentTerm.term;

    setIsCorrect(correct);

    if (correct) {
      fireworksEffect();
      setScore((prevScore) => prevScore + 1);
      setShowNextButton(true);
      shuffleTags(); // Перемешиваем теги при правильном ответе

      // Автоматически переходим к следующему определению после короткой паузы
      setTimeout(() => {
        if (currentIndex < terms.length - 1) {
          setCurrentIndex((prevIndex) => prevIndex + 1);
          setDroppedTerm(null);
          setIsCorrect(null);
          setShowNextButton(false);
        } else {
          // Завершаем игру если это было последнее определение
          setCompleted(true);

          // Уведомляем родительский компонент о завершении
          if (onChange) {
            onChange(
              JSON.stringify({
                completed: true,
                score: score + 1, // Добавляем текущий балл за правильный ответ
                total: terms.length,
              })
            );
          }
        }
      }, 1500); // Даем время пользователю увидеть сообщение об успехе
    } else {
      shuffleTags(); // Перемешиваем теги при неправильном ответе

      // При неправильном ответе переходим к следующему определению
      setTimeout(() => {
        if (currentIndex < terms.length - 1) {
          setCurrentIndex((prevIndex) => prevIndex + 1);
          setDroppedTerm(null);
          setIsCorrect(null);
        } else {
          // Завершаем игру если это было последнее определение
          setCompleted(true);

          // Уведомляем родительский компонент о завершении
          if (onChange) {
            onChange(
              JSON.stringify({
                completed: true,
                score,
                total: terms.length,
              })
            );
          }
        }
      }, 1500); // Даем время пользователю увидеть сообщение об ошибке
    }
  };

  // Обработчик перехода к следующему определению
  const handleNextDefinition = () => {
    if (currentIndex < terms.length - 1) {
      setCurrentIndex((prevIndex) => prevIndex + 1);
      setDroppedTerm(null);
      setIsCorrect(null);
      setShowNextButton(false);
    } else {
      // Завершаем игру
      setCompleted(true);

      // Уведомляем родительский компонент о завершении
      if (onChange) {
        onChange(
          JSON.stringify({
            completed: true,
            score: score + (isCorrect ? 1 : 0), // Добавляем текущий балл, если ответ правильный
            total: terms.length,
          })
        );
      }
    }
  };

  // Рестарт игры
  const handleRestart = () => {
    setCurrentIndex(0);
    setDroppedTerm(null);
    setIsCorrect(null);
    setCompleted(false);
    setScore(0);
    setShowNextButton(false);
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (terms.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          {t('tagCloud.loading')}
        </Typography>
      </Box>
    );
  }

  if (completed) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h5" gutterBottom>
          {t('tagCloud.congratulations')}
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          {t('tagCloud.yourResult', {
            score: score,
            total: terms.length,
            percentage: Math.round((score / terms.length) * 100),
          })}
        </Typography>
        <Button variant="contained" color="primary" onClick={handleRestart}>
          {t('tagCloud.restart')}
        </Button>
      </Box>
    );
  }

  const currentDefinition = terms[currentIndex]?.definition;

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom color="text.secondary">
          {t('tagCloud.definitionCounter', {
            current: currentIndex + 1,
            total: terms.length,
          })}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={(currentIndex / terms.length) * 100}
          sx={{ mb: 1 }}
        />
      </Box>

      {/* Облако тегов */}
      <Box
        ref={cloudRef}
        sx={{
          position: 'relative',
          height: '250px',
          mb: 3,
          backgroundColor: '#f5f5f5',
          borderRadius: '8px',
          padding: 2,
          overflow: 'hidden',
        }}
      >
        {cloudTags.map((tag, index) => (
          <CloudTag
            key={`${tag.term}-${index}`}
            fontSize={tag.fontSize}
            rotate={tag.rotate}
            color={tag.color}
            draggable
            onDragStart={(e) => handleDragStart(e, tag.term)}
            onDragEnd={handleDragEnd}
            sx={{
              position: 'absolute',
              left: tag.left,
              top: tag.top,
              zIndex: tag.zIndex,
            }}
          >
            {tag.term}
          </CloudTag>
        ))}
      </Box>

      <Typography variant="h6" gutterBottom align="center">
        {t('tagCloud.dragMatchingTerm')}
      </Typography>

      <DefinitionCard elevation={3}>
        <Typography variant="body1" sx={{ textAlign: 'center', p: 2 }}>
          {currentDefinition}
        </Typography>
      </DefinitionCard>

      {/* Область для перетаскивания */}
      <DropArea
        sx={{
          border: `2px dashed ${
            isDragOver
              ? (theme) => theme.palette.primary.main
              : (theme) => theme.palette.divider
          }`,
          backgroundColor:
            isCorrect === true
              ? (theme) => theme.palette.success.light
              : isCorrect === false
              ? (theme) => theme.palette.error.light
              : isDragOver
              ? (theme) => theme.palette.primary.light + '20'
              : (theme) => theme.palette.background.default,
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {droppedTerm ? (
          <Typography varButtoniant="h6">{droppedTerm}</Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('tagCloud.dropHere')}
          </Typography>
        )}
      </DropArea>

      <Box
        sx={{
          mb: 2,
          minHeight: '60px',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        {isCorrect === true && (
          <Fade in={isCorrect === true}>
            <Alert severity="success" sx={{ mb: 2 }}>
              {t('tagCloud.correctAnswer')}
            </Alert>
          </Fade>
        )}

        {isCorrect === false && (
          <Fade in={isCorrect === false}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {t('tagCloud.wrongAnswer')}
            </Alert>
          </Fade>
        )}
      </Box>

      {showNextButton && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleNextDefinition}
          >
            {currentIndex < terms.length - 1
              ? t('tagCloud.nextDefinition')
              : t('tagCloud.finish')}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default TagCloud;
